import * as React from 'react'
import {connect, Store} from "react-redux"
import thunkMiddleware from "redux-thunk"
import {Settings, Params} from "./settings-form"
import {SettingsForm, DataProps, CallbackProps} from "./presentation"
import {createStore, applyMiddleware, Action, Dispatch} from "redux"
import {CommandCaller} from "../../../lib/commands"
import {AsyncAction} from "../../../lib/types"

export const createStoreFromParams = (params:Params) =>
	createStore<State>(
		reducer,
		{settings: params.settings, isSaving: false, publicUrl: params.publicUrl},
		applyMiddleware(thunkMiddleware)
	)

export type State = {
	settings: Settings
	isSaving: boolean
	publicUrl: string
}

const defaultSettings:Settings = {
	id:       0,
	instance: null,
	username: "",
	password: "",
}

const defaultState:State = {
	isSaving: false,
	settings: defaultSettings,
	publicUrl: ""
}

type ChangeUsernameAction = {type: "change username", to: string}
type ChangePasswordAction = {type: "change password", to: string}
type SettingsAction = ChangeUsernameAction | ChangePasswordAction
type SettingsActionWrapper = {type: "settings changed", action: SettingsAction}

type SaveActionStart = {type: "saving settings"}
type SaveActionSuccess = {type: "settings saved", settings: Settings}
type SaveActionFailure = {type: "saving settings failed"}
type SaveAction = SaveActionStart | SaveActionSuccess | SaveActionFailure
type SaveActionWrapper = {type: "save info", action: SaveAction}
type SillyType = {type: "i don't want you to actually implement this type"}

export const settingsReducer = (s:Settings = defaultSettings, action:SettingsAction):Settings => {
	switch (action.type) {
		case 'change username':
			return {username: action.to, id: s.id, instance: s.instance, password: s.password}

		case 'change password':
			return {password: action.to, id: s.id, instance: s.instance, username: s.username}
	}
}

export const saveReducer = (state:State = defaultState, action:SaveAction):State => {
	switch(action.type) {
		case "saving settings":
			return {settings: state.settings, publicUrl: state.publicUrl, isSaving: true}

		case "settings saved":
			return {
				isSaving: false,
				publicUrl: state.publicUrl,
				settings: (action as SaveActionSuccess).settings
			}

		case "saving settings failed":
			return {settings: state.settings, publicUrl: state.publicUrl, isSaving: false}
	}
}

export const reducer = (state:State = defaultState, action: SettingsActionWrapper | SaveActionWrapper | SillyType):State => {
	switch(action.type) {
		// Unwrap the action because inside it will be another action that says specifically what setting has changed and how.
		case "settings changed":
			return {
				...state,
				settings: settingsReducer(state.settings, action.action)
			}

		// Async save settings actions redirected through here.
		case "save info":
			return saveReducer(state, action.action)
		default:
			return state;
	}
}

const wrapSettingsChangeAction = (action:SettingsAction):SettingsActionWrapper => ({
	type: "settings changed",
	action: action
})

const wrapSaveAction = (action:SaveAction):SaveActionWrapper => ({
	type: "save info",
	action: action
})

export const actionCreators = {

	settings: {
		usernameChange: (username:string):SettingsActionWrapper => wrapSettingsChangeAction({type: "change username", to: username}),
		passwordChange: (password:string):SettingsActionWrapper => wrapSettingsChangeAction({type: "change password", to: password}),
	},

	save: {
		saveStart: ():SaveActionWrapper => wrapSaveAction({type: "saving settings"}),
		saveFailure: ():SaveActionWrapper => wrapSaveAction({type: "saving settings failed"}),
		saveSuccess: (settings:Settings):SaveActionWrapper => wrapSaveAction({type: "settings saved", settings: settings}),
	},

	saveSettings: (commandCaller:CommandCaller, settings:Settings): AsyncAction<Action,{}> => {
		return async (dispatch:Dispatch<{}>) => {
			dispatch(actionCreators.save.saveStart())
			try {
				const result:Settings = await commandCaller.runPost("save.settings", settings) as Settings
				dispatch(actionCreators.save.saveSuccess(result))
			} catch (e) {
				dispatch(actionCreators.save.saveFailure())
			}
		}
	}
}

const mapStateToProps = (state:State):DataProps => state

const mapDispatchToProps = (dispatch:(a: any) => Action, ownProps:OwnProps):CallbackProps => {
	return {
		onUsernameChange: (username: string) => dispatch(actionCreators.settings.usernameChange(username)),
		onPasswordChange: (password:string) => dispatch(actionCreators.settings.passwordChange(password)),
		onSave: (settings:Settings) => dispatch(actionCreators.saveSettings(new CommandCaller(ownProps.ajaxUrl), settings)),
	}
}

type OwnProps = {ajaxUrl: string, store: Store<State>}

export const SettingsFormContainer = connect<DataProps, CallbackProps, OwnProps, State>(
	mapStateToProps,
	mapDispatchToProps
)(SettingsForm)



// WEBPACK FOOTER //
// ./src/3rd party integration/wentworthadvantage/settings-form/app.tsx